<script setup lang="ts">
import { computed, defineProps, ref, watch } from "vue";

type NavLink = { label: string; url: string; target?: string };

const props = defineProps( {
  discoverThemes: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  discussThemes: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  mainNavigationLinks: {
    type: Array as PropType<NavLink[]>,
    default: () => [],
  },
  loggedInMainNavigationLinks: {
    type: Array as PropType<NavLink[]>,
    default: () => [],
  },
  showCpdLink: {
    type: Boolean,
    default: false,
  },
  subscriberCpdLink: {
    type: Object as PropType<NavLink | null>,
    default: null,
  },
  nonSubscriberCpdLink: {
    type: Object as PropType<NavLink | null>,
    default: null,
  },
  nonSubscriberJoinButton: {
    type: Boolean,
    default: false,
  },
  logoLinkText: {
    type: String,
    default: null,
  },
  skipLinkText: {
    type: String,
    default: null,
  },
} );

const orderResource = useOrderResource();
const { data: purchasedProducts, refresh } = await orderResource.listPurchasedProductsForWebAccount();

// Define member first
const auth = useAuthStore();

watch( () => auth.isLoggedIn, async () => {
  await refresh();
} );

const showSidebar = ref( false );

const items = computed( () => {
  const itemsArray = [];

  if ( auth.isLoggedIn ) {
    for ( const link of props.loggedInMainNavigationLinks ) {
      itemsArray.push( {
        label: link.label,
        url: link.url,
        target: link.target,
        bg: true,
      } );
    }

    // Whether the member has no purchased subscriptions
    const isNonSubscriber = purchasedProducts.value.length === 0;

    // Conditional CPD link based on subscriptionType
    if ( props.showCpdLink ) {
      if ( !isNonSubscriber ) {
        // User has a subscription type - show subscriber CPD
        if ( props.subscriberCpdLink ) {
          itemsArray.push( {
            label: props.subscriberCpdLink.label,
            url: props.subscriberCpdLink.url,
            bg: true,
          } );
        }
      } else {
        // User has no subscription type or it's null - show non-subscriber CPD
        if ( props.nonSubscriberCpdLink ) {
          itemsArray.push( {
            label: props.nonSubscriberCpdLink.label,
            url: props.nonSubscriberCpdLink.url,
            bg: true,
          } );
        }
      }
    }

    // Only show the Join button to logged-in members who aren't yet subscribers, when enabled
    if ( props.nonSubscriberJoinButton && isNonSubscriber ) {
      itemsArray.push( {
        label: "Join",
        url: "/register",
        uppercase: true,
      } );
    }
  } else {
    for ( const link of props.mainNavigationLinks ) {
      itemsArray.push( {
        label: link.label,
        url: link.url,
        target: link.target,
        uppercase: false,
      } );
    }

    itemsArray.push( {
      label: "Join",
      url: "/register?step=join",
      uppercase: true,
    } );
  }

  return itemsArray;
} );

const profileItems = computed( () => {
  const result = [];

  if ( auth.isLoggedIn ) {
    result.push( {
      label: "My account",
      url: "/my-account",
    } );

    result.push( {
      label: "Log out",
      url: "/logout",
    } );
  }

  return result;
} );
</script>

<template>
  <div class="site-header-wrapper">
    <header class="site-header">
      <a
        class="sr-only"
        href="#main">{{ skipLinkText || "Skip to main content" }}</a>

      <div class="container">
        <div class="site-header__content">
          <nuxt-link
            id="site-header__link-logo"
            class="site-header__logo"
            :title="logoLinkText || 'Back to home'"
            to="/">
            <SiteLogo />
          </nuxt-link>

          <div class="site-header__nav site-header__nav--mobile">
            <a-button
              id="site-header__nav-button-toggle"
              variant="link"
              class="site-header__toggle"
              aria-label="Open site navigation"
              @click="showSidebar = true">
              <a-icon
                icon="fa:light:bars"
                size="2rem" />
            </a-button>
          </div>

          <SiteDesktopNavigation
            :items="items"
            :profile-items="profileItems"
            class="site-header__nav site-header__nav--desktop" />
        </div>
      </div>
    </header>

    <!-- Mobile Sidebar -->
    <!-- Backdrop fades -->
    <transition name="overlay-fade">
      <div
        v-if="showSidebar"
        class="sidebar-overlay"
        @click="showSidebar = false" />
    </transition>

    <!-- Sheet slides -->
    <transition name="sheet-slide">
      <div
        v-if="showSidebar"
        class="sidebar-content">
        <a-button
          variant="link"
          class="close-button"
          aria-label="Close navigation"
          @click="showSidebar = false">
          <a-icon
            icon="fa:light:times"
            size="2rem" />
        </a-button>

        <SiteMobileNavigation
          :items="items"
          :profile-items="profileItems"
          @close="showSidebar = false" />
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.site-header {
  background: #201D25;
  z-index: 999;
  margin-bottom: 20px;
  position: relative;

  &__content {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    padding: 0;
    // border-bottom: 1px solid #f2f2f2;

    @media (max-width: 991px) {
      padding: 5px 0;
    }
  }

  &__logo {
    display: flex;
    align-items: center;
    font-weight: 400;
    color: var(--primary);
    font-size: 1.5rem;
    padding: 10px 0;
    text-decoration: none !important;

    @media (max-width: 768px) {
      padding-top: 0;
    }
  }

  &__toggle {
    padding: 5px 10px;
    margin-right: 0;
    font-size: 0.9rem;

    @media (max-width: 321px) {
      svg {
        font-size: 1.5rem;
      }
    }
  }

  &__nav {
    &--mobile {
      display: none;
      align-items: center;
    }

    @media (max-width: 1000px) {
      &--mobile {
        display: flex !important;
      }

      &--desktop {
        display: none !important;
      }
    }
  }
}

.dark-mode .site-header {
  background: #201D25;

  &__content {
    border-bottom-color: var(--dark-border-color);
  }
}

.btn.btn-link.site-header__toggle {
  color: #fff !important;;
}

/* -----------------  Backdrop  ----------------- */
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 999;
}

/* Backdrop fades */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}
.overlay-fade-enter-to,
.overlay-fade-leave-from {
  opacity: 1;
}

/* -----------------  Sheet  ----------------- */
.sidebar-content {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 80%;
  max-width: 350px;
  height: 100%;
  padding: 20px;
  overflow-y: auto;
  background: #fff;
  z-index: 1000;
  will-change: transform; /* better GPU perf */
}

/* Sheet slides from the right */
.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 0.3s ease;
}
.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateX(100%);
}
.sheet-slide-enter-to,
.sheet-slide-leave-from {
  transform: translateX(0);
}

/* Close button */
.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1001;
}
</style>
